import { useCallback, useEffect, useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';

import {
  useGetFreeFloorPlanEligibilityQuery,
  useGetPrimaryServicesQuery
} from '../../services/orderApi';
import {
  buildFloorPlanOffer,
  freeFloorPlanImage,
  freeFloorPlanQualifies,
  isFreeTier,
  type FloorPlanOffer,
  type FloorPlanTier
} from './floorPlanOffer';
import {
  toFloorPlanCoverage,
  type FloorPlanCoverage
} from './floorPlanCoverage';
import { track } from '@/services/telemetry';

export interface FreeFloorPlanOffer {
  offer: FloorPlanOffer;
  /** Does the rollout cover the account (DV-3697)? */
  coverage: FloorPlanCoverage;
  /**
   * The tiers to offer a property in the account with these services, or
   * null when it gets no free floor plan.
   */
  tiersFor: (slugs: string[]) => FloorPlanTier[] | null;
  /** The free tier such a property gets, or null: the cart's line. */
  freeTierFor: (slugs: string[]) => FloorPlanTier | null;
}

/**
 * The floor plan tier choice for one account's properties in the cart
 * (DV-3204), from the package list (`servicesType=primary`). It is the request
 * the service slider makes, so the cart page and the slider share one
 * response: each catalog read costs products-api seconds today (an unindexed
 * price lookup per service), and the slider then opens from the cart page's
 * copy (`useServiceSelection`).
 *
 * The package list holds every service without prerequisites, which includes
 * the flagged packages and the tiers. Should a tier ever be given
 * prerequisites, it drops out of this list and is reported as missing
 * (`floor-plan-offer-degraded`), rather than silently disappearing.
 *
 * Who gets it comes from order-service (DV-3697): the cart offers the free
 * plan only in an account the rollout covers, and only once that answer is
 * in. One account per call — the cart calls it once per account it groups
 * its lines by — so each read is an ordinary generated query, cached per
 * account and shared with the slider.
 */
export function useFreeFloorPlanOffer({
  entityUUID,
  skip = false
}: {
  /** The account whose properties may be offered the free plan. */
  entityUUID: string | undefined;
  /** Skip the reads when nothing on the page could use the offer. */
  skip?: boolean;
}): FreeFloorPlanOffer {
  const { data, isError } = useGetPrimaryServicesQuery(undefined, { skip });
  const eligibility = useGetFreeFloorPlanEligibilityQuery(
    skip || !entityUUID ? skipToken : entityUUID
  );

  const offer = useMemo<FloorPlanOffer>(() => {
    // RTK keeps the last good list when a refetch fails; keep offering from it
    // rather than dropping the panel and the cart lines mid-session.
    if (data) return buildFloorPlanOffer(data);
    if (isError) return { status: 'unavailable', reason: 'catalog-error' };
    return { status: 'loading' };
  }, [data, isError]);

  // `currentData`, not `data`: `data` would carry the previous account's
  // answer while this one's loads.
  const coverage = useMemo(
    () =>
      toFloorPlanCoverage({
        data: eligibility.currentData,
        error: eligibility.error
      }),
    [eligibility.currentData, eligibility.error]
  );

  // DV-3204: the failure is visible to ops rather than silent. The cart itself
  // carries on: an unavailable offer hides the panel, and a missing paid tier
  // is left out of it.
  useEffect(() => {
    if (offer.status === 'unavailable') {
      track('floor-plan-offer-degraded', {
        reason: offer.reason,
        missingTierSlugs: ''
      });
    } else if (
      offer.status === 'available' &&
      offer.missingTierSlugs.length > 0
    ) {
      track('floor-plan-offer-degraded', {
        reason: 'tiers-missing',
        missingTierSlugs: offer.missingTierSlugs.join(',')
      });
    }
  }, [offer]);

  // DV-3697: order-service could not say whether the rollout covers the
  // account, so the cart offered nothing there. Same event, so ops watch one
  // place; `entityUUID` names the account.
  useEffect(() => {
    if (coverage.status === 'undecided' && entityUUID) {
      track('floor-plan-offer-degraded', {
        reason: 'eligibility-undecided',
        entityUUID,
        cause: coverage.cause
      });
    }
  }, [coverage, entityUUID]);

  const tiersFor = useCallback(
    (slugs: string[]) => {
      if (offer.status !== 'available' || coverage.status !== 'covered') {
        return null;
      }
      return freeFloorPlanQualifies(offer, slugs, coverage.floorPlanSlugs)
        ? offer.tiers
        : null;
    },
    [offer, coverage]
  );

  const freeTierFor = useCallback(
    (slugs: string[]) => tiersFor(slugs)?.find(isFreeTier) ?? null,
    [tiersFor]
  );

  return { offer, coverage, tiersFor, freeTierFor };
}

/**
 * The free Basic Floor Plan's card image (DV-3808), which checkout hands to
 * order confirmation: its image lookup is built from the cart's services,
 * and the free line is never one of them. The same package-list read as the
 * offer, so it costs no request of its own.
 */
export function useFreeFloorPlanImage({
  skip = false
}: {
  skip?: boolean;
}): string | null {
  const { data } = useGetPrimaryServicesQuery(undefined, { skip });
  return useMemo(() => (data ? freeFloorPlanImage(data) : null), [data]);
}
